import React, { useState, useEffect, useRef } from "react";

import { cloneDeep, isEmpty, isEqual } from "lodash";
import { Trans, useTranslation } from "react-i18next";
import { AgGridColumn, AgGridReact } from "ag-grid-react";

import DropdownButton from "../Buttons/DropdownButton";
import { SuccessAlert } from "../../utils/Alerts";
import FilterDropdown from "../Dropdown/FilterDropdown";
import OptionsDropdown from "../Dropdown/OptionsDropdown";
import MeasurementDeleteModal from "../Modals/MeasurementDeleteModal";
import MeasurementsAPI from "../../API/measurements.api";
import ScansAPI from "../../API/scans.api";
import { shortDateTime, shortDateYMD } from "../../utils/DateHelper";
import { getCurrentDateYMD } from "../../utils/DateHelper";
import {
    DATE_FILTER_TYPE,
    FILTER_TYPES,
    MEASUREMENT_METADATA,
    COLLECT_METADATA_COLUMNS,
    TEXT_FILTER_TYPE,
} from "../../utils/constants";
import LoadingOverlay from "../Loaders/LoadingOverlay";
import LogCapture from "../../utils/LogManagement/LogHelper";
import BaseModal from "../Modals/BaseModal";
import SpectrumChart from "../Charts/SpectrumChart";
import { CreateSpectrumPoints } from "../../utils/CreatePoints";
import { SPECTRUM_POINTS_BOUNDARY } from "../../utils/constants";
import MeasurementsDetailsModal from "../Modals/MeasurementsDetailsModal";
import VerticalRadioButtons from "../RadioButtons/VerticalRadioButtons";

const DataTable = ({
    visible,
    data,
    selectedRows,
    setSelectedRows,
    setData,
    selectedSpectrumPoints,
    setSelectedSpectrumPoints,
    modifiedDataReference,
    setModifiedDataReference,
    setIsFilterUsed,
    setFilteredRows,
    isWavenumber,
    setIsWavenumber,
    isReflectance,
    setIsReflectance,
    xAxisCheckedReference,
    yAxisCheckedReference,
    setXAxisCheckedReference,
    setYAxisCheckedReference,
    showSpectraOptions = true,
}) => {
    const { t } = useTranslation();

    const [, setHeaders] = useState([]);
    const [mixedData, setMixedData] = useState(null);
    const [gridApi, setGridApi] = useState(null);
    const [gridColumnApi, setGridColumnApi] = useState(null);
    const [isDataHidden, setIsDataHidden] = useState(false);
    const [isReferenceHidden, setIsReferenceHidden] = useState(false);
    const [isMetadataHidden, setIsMetadataHidden] = useState(false);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [spectraModalVisible, setSpectraModalVisible] = useState(false);
    const [dataIds, setDataIds] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [detailsModalVisible, setDetailsModalVisible] = useState(false);
    const [selectedRow, setSelectedRow] = useState({});
    const [isSpectrumModalDisabled, setIsSpectrumModalDisabled] =
        useState(false);

    const [filters, setFilters] = useState({});
    const [appliedFilters, setAppliedFilters] = useState({});

    const exportRef = useRef();

    const [options, setOptions] = useState({
        hideMetadata: false,
        hideSpectralData: false,
        hideReferenceData: false,
    });

    const [filterOptions, setFilterOptions] = useState({
        createdAt: {
            label: t("createdAt"),
            name: "createdAt",
            type: "Date",
            max: getCurrentDateYMD(),
            disabled: true,
        },
        scanner: {
            label: t("scannerId"),
            name: "scanner",
            type: "Dropdown",
            options: [],
            disabled: true,
        },
        sampleName: {
            label: t("sampleName"),
            name: "sampleName",
            type: "Input",
            placeholder: t("sampleNamePlaceholder"),
        },
        createdBy: {
            label: t("createdBy"),
            name: "createdBy",
            type: "Dropdown",
            options: [],
            disabled: true,
        },
    });

    const xAxisOptions = [
        {
            label: `${t("wavenumber")} (cm⁻¹)`,
            value: "wavenumber",
        },
        {
            label: `${t("wavelength")} (nm)`,
            value: "wavelength",
        },
    ];

    const yAxisOptions = [
        {
            label: t("reflectance"),
            value: "reflectance",
        },
        {
            label: t("absorbance"),
            value: "absorbance",
        },
    ];

    const SPECTRA_OPTIONS = [
        {
            element: (closeOptionsButton) => (
                <div>
                    <div className="border-bottom">
                        <div className="px-3 py-1">X-axis</div>
                    </div>
                    <div className=" m-2">
                        <VerticalRadioButtons
                            name={"xAxis"}
                            options={xAxisOptions}
                            onChangeHandler={(e) => {
                                spectraRadioHandler(
                                    e,
                                    setXAxisCheckedReference,
                                    isWavenumber,
                                    setIsWavenumber
                                );
                                closeOptionsButton();
                                gridApi.showLoadingOverlay();
                            }}
                            checkedReference={xAxisCheckedReference?.value}
                        />
                    </div>
                </div>
            ),
        },
        {
            element: (closeOptionsButton) => (
                <div>
                    <div className="border-bottom">
                        <div className="px-3 py-1">Y-axis</div>
                    </div>
                    <div className="m-2">
                        <VerticalRadioButtons
                            name={"yAxis"}
                            options={yAxisOptions}
                            onChangeHandler={(e) => {
                                spectraRadioHandler(
                                    e,
                                    setYAxisCheckedReference,
                                    isReflectance,
                                    setIsReflectance
                                );
                                closeOptionsButton();
                                gridApi.showLoadingOverlay();
                            }}
                            checkedReference={yAxisCheckedReference?.value}
                        />
                    </div>
                </div>
            ),
        },
    ];

    const optionsList = [
        {
            label: t("hideMetadata"),
            name: "hideMetadata",
        },
        {
            label: t("hideSpectralData"),
            name: "hideSpectralData",
        },
        {
            label: t("hideReferenceData"),
            name: "hideReferenceData",
        },
    ];

    const CREATED_BY_COL = "createdAt";
    const dropdownButtonOptions = [
        {
            icon: "file-download",
            label: t("export"),
            callback: () => {
                exportTable();
            },
        },
        {
            icon: "brain fa-xs",
            label: t("eigenvector"),
            callback: () => {
                if (isWavenumber) {
                    setIsWavenumber(false);
                    exportRef.current = true;
                } else exportTable();
            },
        },
    ];

    const fields = [
        { label: t("createdBy"), name: "createdBy", type: "email" },
        { label: t("collectedBy"), name: "collectedBy", type: "email" },
        { label: t("createdAt"), name: "createdAt", type: "datetime" },
        { label: t("materialName"), name: "materialName", type: "text" },
        { label: t("scannerId"), name: "scanner", type: "text" },
        { label: t("scanTime"), name: "scanTime", type: "text" },
        { label: t("longitude"), name: "longitude", type: "text" },
        { label: t("latitude"), name: "latitude", type: "text" },
    ];

    useEffect(() => {
        const isFilterUsed = !isEmpty(appliedFilters);
        setIsFilterUsed(isFilterUsed);
        updateFilteredRows(isFilterUsed);
    }, [appliedFilters]);

    useEffect(() => {
        if (!isEmpty(selectedRows)) {
            if (selectedRows.length <= SPECTRUM_POINTS_BOUNDARY) {
                setSelectedSpectrumPoints(
                    CreateSpectrumPoints(getSelectedRowsData())
                );
                setIsSpectrumModalDisabled(false);
            } else setIsSpectrumModalDisabled(true);
        }
    }, [selectedRows, data]);

    useEffect(() => {
        if (!mixedData) {
            setIsLoading(true);
        }
    }, [mixedData]);

    useEffect(() => {
        if (data && selectedRows && gridApi) {
            let nodes = [];
            // remove the unselected
            gridApi.getSelectedNodes().forEach((node) => {
                if (!selectedRows.includes(node.id)) {
                    node.setSelected(false);
                    nodes.push(node);
                }
            });

            // mark as selected
            for (const id of selectedRows) {
                const node = gridApi.getRowNode(id);
                if (node) {
                    node.setSelected(true);
                    nodes.push(node);
                }
            }
        }
    }, [selectedRows]);

    useEffect(() => {
        if (data && modifiedDataReference) generateData();
    }, [data, modifiedDataReference]);

    useEffect(() => {
        hideorShowColumn("metadata", !isMetadataHidden);
    }, [isMetadataHidden, gridColumnApi]);

    useEffect(() => {
        hideorShowColumn("reference", !isReferenceHidden);
    }, [isReferenceHidden, gridColumnApi]);

    useEffect(() => {
        hideorShowColumn("data", !isDataHidden);
    }, [isDataHidden, gridColumnApi]);

    const hideorShowColumn = (key, state) => {
        if (gridColumnApi && gridApi) {
            const colDef = gridApi.getColumnDefs();
            const group = colDef.find((c) => c.field === key);
            const children = group?.children?.map((c) => c.colId);
            if (children) {
                gridColumnApi.setColumnsVisible(children, state);
            } else {
                gridColumnApi.setColumnVisible(group, false);
            }
        }
    };

    const spectraRadioHandler = (e, referenceSetter, flag, flagSetter) => {
        flagSetter(!flag);
        referenceSetter(e.target);
    };

    const getSelectedRowsData = () => {
        const selectedDictionary = {};
        selectedRows.forEach((row) => {
            const splitted = row.split("_")[0];
            selectedDictionary[splitted] = splitted;
        });

        let selectedData = cloneDeep(data);
        selectedData = { ...selectedData, ids: [], rows: [] };

        data.ids.forEach((row, index) => {
            if (selectedDictionary[row?.id]) {
                selectedData.ids.push(row);
                selectedData.rows.push(data.rows[index]);
            }
        });

        return selectedData;
    };

    const updateFilteredRows = () => {
        const isFilterUsed = !isEmpty(appliedFilters);
        setIsFilterUsed(isFilterUsed);
        if (isFilterUsed && gridApi) {
            let rowData = [];
            gridApi.forEachNodeAfterFilter((node) => {
                rowData.push(node.data[node.data.length - 2]);
            });
            setFilteredRows(rowData);
        }
    };

    const handleRowsUpdated = () => {
        setTimeout(() => {
            updateFilteredRows(!isEmpty(gridApi?.getFilterModel() || {}));
        }, 500);
    };

    const generateData = () => {
        setIsLoading(true);
        concatDataWithReference(data, modifiedDataReference);
    };

    const getDropdownValues = (
        retrievedData,
        index,
        metadataKeys,
        dropdownValues
    ) => {
        const DROPDOWNS = ["scanner", "createdBy"];
        const rowMetadata = [];

        for (const key of metadataKeys) {
            rowMetadata.push(retrievedData.metadata[key][index] || "--");
            // add values in dropdown
            if (DROPDOWNS.includes(key) && retrievedData.metadata[key][index]) {
                if (!dropdownValues[key]) dropdownValues[key] = {};
                dropdownValues[key][retrievedData.metadata[key][index]] = true;
            }
        }

        return rowMetadata;
    };

    const concatDataWithReference = (retrievedData, reference) => {
        const copiedData = JSON.parse(JSON.stringify(retrievedData)); // Deep clone so we dont modify actual data
        const metadataKeys = Object.keys(retrievedData.metadata);
        const dropdownValues = {};
        const newFilterOptions = { ...filterOptions };

        const headers = [
            "Sample Name",
            ...metadataKeys,
            ...copiedData.headers,
            ...(reference.length > 0 ? reference.headers : []),
        ];

        copiedData.rows.forEach((row, index) => {
            const referenceRow =
                reference.rows[index] ||
                new Array(reference.headers.length).fill("--");

            const rowMetadata = getDropdownValues(
                retrievedData,
                index,
                metadataKeys,
                dropdownValues
            );

            copiedData.rows[index] = [
                ...row.slice(0, 1),
                ...rowMetadata,
                ...row.slice(1),
                ...(referenceRow ? referenceRow : []),
                index,
                `${copiedData.ids[index]?.id || copiedData.ids[index]}_${
                    copiedData.ids[index]?.key || ""
                }`,
            ];
        });

        for (const key in COLLECT_METADATA_COLUMNS) {
            if (dropdownValues[key]) {
                const retrievedOptions = Object.keys(dropdownValues[key]);
                newFilterOptions[key].options = retrievedOptions.map(
                    (value) => {
                        return { value, label: value };
                    }
                );
            }
            if (newFilterOptions[key]) newFilterOptions[key].disabled = false;
        }

        setIsLoading(false);
        setHeaders([...headers]);
        setDataIds(copiedData.ids);
        setMixedData([...copiedData.rows]);
        setFilterOptions({ ...newFilterOptions });
        handleRowsUpdated();
        // if (gridApi) {
        //     gridApi.setRowData(mixedData);
        //     gridApi.redrawRows();
        // }
    };

    const handleDelete = async (isLocal) => {
        try {
            if (!isLocal) {
                const scanRows = {};
                const measurementRows = [];
                let count = 0;
                gridApi.getSelectedNodes().forEach((row) => {
                    const _index = row.data[row.data.length - 2];
                    const newSelectedRow = dataIds[_index];
                    const id = newSelectedRow?.id || newSelectedRow;

                    // it's a scan measurmeent
                    if (newSelectedRow?.key) {
                        if (!scanRows[id]) {
                            scanRows[id] = [];
                        }
                        scanRows[id].push(newSelectedRow.key);
                    } else {
                        measurementRows.push(id);
                    }
                    count++;
                });

                if (!isEmpty(measurementRows)) {
                    await MeasurementsAPI.deleteBulkMeasurements(
                        measurementRows
                    );
                    LogCapture.info(
                        `Bulk measurements are deleted with ids: ${measurementRows}`
                    );
                }

                if (!isEmpty(scanRows)) {
                    await ScansAPI.deleteScanMeasurements(scanRows);
                    LogCapture.info(
                        `Bulk scan measurements deleted ${JSON.stringify(
                            scanRows
                        )}`
                    );
                }

                SuccessAlert(
                    t("deleteScansSuccessMessage", {
                        count,
                    })
                );
            }
            const retrievedSelectedRows = gridApi
                .getSelectedNodes()
                .map((n) => n.data[n.data.length - 2]);
            const newRows = [...data?.rows];
            const newMetadata = { ...data?.metadata };
            const newIds = [...data?.ids];
            const newReference = [...modifiedDataReference?.rows];

            retrievedSelectedRows.sort((a, b) => a - b);
            // splice from the array each index in reverse order to keep the indices correct
            for (let i = retrievedSelectedRows.length - 1; i >= 0; i--) {
                const rowIndex = retrievedSelectedRows[i];
                newRows.splice(rowIndex, 1);
                newIds.splice(rowIndex, 1);
                newReference && newReference.splice(rowIndex, 1);
                for (const key of Object.keys(newMetadata)) {
                    newMetadata[key].splice(rowIndex, 1);
                }
            }

            setData({
                ...data,
                rows: newRows,
                ids: newIds,
                metadata: newMetadata,
            });
            setModifiedDataReference({
                ...modifiedDataReference,
                rows: newReference,
            });
            cancelSelection();
            return true;
        } catch (e) {
            console.log(e);
            return e;
        }
    };

    const cancelSelection = () => {
        if (gridApi) {
            gridApi.deselectAll();
            setSelectedRows([]);
        }
    };

    const exportTable = () => {
        gridApi.exportDataAsCsv({
            allColumns: true,
            skipColumnGroupHeaders: true,
            processCellCallback: (cellParams) => {
                if (cellParams && cellParams?.column?.colId === "createdAt") {
                    return new Date(cellParams?.value).toISOString();
                }
                return cellParams?.value;
            },
            processHeaderCallback: (params) => {
                if (params && params?.column?.colId === "createdAt") {
                    return "Created At (UTC)";
                }
                return params?.column?.colDef?.headerName;
            },
        });
        LogCapture.info("Measurements are exported successfully");
    };

    const formatDate = (date, addDays = 0) => {
        if (date) {
            return shortDateYMD(date, addDays);
        }
    };

    const generateDateConditions = (date, type) => {
        if (date) {
            return {
                condition1: {
                    filterType: FILTER_TYPES.date,
                    type,
                    dateFrom: formatDate(date),
                },
                condition2: {
                    filterType: FILTER_TYPES.date,
                    type: DATE_FILTER_TYPE.equal,
                    dateFrom: formatDate(date),
                },
                filterType: FILTER_TYPES.date,
                operator: "OR",
            };
        }
    };

    const getFilterProps = (filterType, type, filter) => {
        return {
            filterType,
            type,
            filter,
        };
    };

    const applyFilters = (newFilters) => {
        if (!gridApi) return;
        const colDefs = gridApi.getColumnDefs();

        const metadataColArray = [
            ...colDefs.find((column) => column.colId === "metadata").children,
        ];

        const metadataCol = { [MEASUREMENT_METADATA.sampleName]: colDefs[0] };
        metadataColArray.forEach((row) => {
            metadataCol[row.colId] = row;
        });

        const filterModel = {};
        Object.keys(newFilters).forEach((filter) => {
            const column = metadataCol[filter];
            handleFilterOptions(filter, column.colId, filterModel, newFilters);
        });

        setFilters({ ...newFilters });
        cancelSelection();
        gridApi.setFilterModel(filterModel);
    };

    const handleFilterOptions = (filter, colId, filterModel, newFilters) => {
        if (filter === MEASUREMENT_METADATA.sampleName) {
            filterModel[colId] = getFilterProps(
                FILTER_TYPES.text,
                TEXT_FILTER_TYPE.contains,
                newFilters[filter]
            );
        }
        if (
            filter === MEASUREMENT_METADATA.scanner ||
            filter === MEASUREMENT_METADATA.createdBy
        ) {
            filterModel[colId] = getFilterProps(
                FILTER_TYPES.text,
                TEXT_FILTER_TYPE.equals,
                newFilters[filter]
            );
        }
        if (filter === MEASUREMENT_METADATA.createdAt) {
            if (newFilters[filter].startDate && newFilters[filter].endDate) {
                filterModel[colId] = {
                    condition1: {
                        filterType: FILTER_TYPES.date,
                        type: DATE_FILTER_TYPE.equal,
                        dateFrom: formatDate(newFilters[filter].startDate),
                    },
                    condition2: {
                        filterType: FILTER_TYPES.date,
                        type: DATE_FILTER_TYPE.inRange,
                        dateFrom: formatDate(newFilters[filter].startDate),
                        dateTo: formatDate(newFilters[filter].endDate, 1),
                    },
                    filterType: FILTER_TYPES.date,
                    operator: "OR",
                };
            } else if (newFilters[filter].startDate) {
                filterModel[colId] = generateDateConditions(
                    newFilters[filter].startDate,
                    DATE_FILTER_TYPE.greaterThan
                );
            } else if (newFilters[filter].endDate) {
                filterModel[colId] = generateDateConditions(
                    newFilters[filter].endDate,
                    DATE_FILTER_TYPE.lessThan
                );
            }
        }
    };

    const applyOptions = (retrievedOptions) => {
        const { hideMetadata, hideSpectralData, hideReferenceData } =
            retrievedOptions;
        setIsMetadataHidden(hideMetadata || false);
        setIsDataHidden(hideSpectralData || false);
        setIsReferenceHidden(hideReferenceData || false);
    };

    const onGridReady = (params) => {
        setGridApi(params.api);
        setGridColumnApi(params.columnApi);
        params.api.showLoadingOverlay();
    };

    const getSelectedRows = () => {
        for (const id of selectedRows) {
            const node = gridApi.getRowNode(id);
            if (node) {
                node.setSelected(true);
            }
        }
    };

    const onSelectionChanged = () => {
        const retrievedSelectedRows = gridApi
            .getSelectedNodes()
            .map((node) => node.id);

        if (!isEqual(retrievedSelectedRows, selectedRows)) {
            setSelectedRows(retrievedSelectedRows);
        }
    };

    const handleRowClicked = (params) => {
        if (data.measurementDetails) {
            setDetailsModalVisible(true);
            const id = params.node.id;
            setSelectedRow({
                ...data.measurementDetails[id],
            });
        }
    };

    const emptyCellStyle = (params) => {
        if (params.value === "--") {
            return { color: "#ff6600" };
        }
        return null;
    };

    const emptyHeaderCellStyle = (params) => {
        const headersLen = modifiedDataReference?.headers?.length;
        if (headersLen) {
            const index =
                Object.keys(data?.metadata)?.length + data?.headers?.length + 1;

            for (let i = 0; i < headersLen; i++) {
                const value = mixedData[params?.node.rowIndex][index + i];
                if (value === "--") {
                    return { backgroundColor: "#fff9f5" };
                }
            }
        }
        return null;
    };

    const getMetadataColumns = () => {
        return Object.keys(data.metadata).map((col, index) => (
            <AgGridColumn
                key={col}
                colId={col}
                filter={
                    col.toLowerCase() === "createdat"
                        ? "agDateColumnFilter"
                        : true
                }
                field={(index + 1).toString()}
                headerName={COLLECT_METADATA_COLUMNS[col] || col}
                cellRenderer={(params) =>
                    col === CREATED_BY_COL
                        ? shortDateTime(params.value)
                        : params.value
                }
                filterParams={{
                    newRowsAction: "keep",
                    comparator:
                        col.toLowerCase() === "createdat"
                            ? (filterLocalDateAtMidnight, cellValue) => {
                                  const dateAsString = cellValue;

                                  if (dateAsString == null) {
                                      return 0;
                                  }

                                  let cellDate = new Date(cellValue);
                                  let comparedToDate = new Date(
                                      filterLocalDateAtMidnight
                                  );
                                  cellDate.setHours(0, 0, 0, 0);
                                  comparedToDate.setHours(0, 0, 0, 0);

                                  // Now that both parameters are Date objects, we can compare
                                  if (cellDate < comparedToDate) {
                                      return -1;
                                  } else if (cellDate > comparedToDate) {
                                      return 1;
                                  }
                                  return 0;
                              }
                            : null,
                }}
            ></AgGridColumn>
        ));
    };

    return (
        <>
            <div
                className={`tabs-content-items flex flex-col mb-5 ${
                    visible ? "show" : ""
                }`}
                id="data-content-wrapper"
            >
                <div className="steps-btn flex items-center justify-end mb-4">
                    <div className="flex">
                        <FilterDropdown
                            filterOptions={Object.values(filterOptions)}
                            filters={filters}
                            setFilters={setFilters}
                            applyFilters={applyFilters}
                            disabled={
                                data &&
                                data.metadata &&
                                !Object.keys(data.metadata).length
                            }
                            component="data-grid"
                            appliedFilters={appliedFilters}
                            setAppliedFilters={setAppliedFilters}
                        />
                        {showSpectraOptions && (
                            <OptionsDropdown
                                optionsList={SPECTRA_OPTIONS}
                                title={t("spectraDataOptions")}
                                clearBtn={false}
                                gridApi={gridApi}
                            />
                        )}
                        <OptionsDropdown
                            optionsList={optionsList}
                            options={options}
                            setOptions={setOptions}
                            applyOptions={applyOptions}
                        />
                        <DropdownButton
                            buttonName={t("export")}
                            options={dropdownButtonOptions}
                        />
                    </div>
                </div>

                <LoadingOverlay active={isLoading} text="Loading...">
                    <div className="grid shadow sm:rounded-lg ag-grid-height">
                        <div
                            className="ag-theme-alpine text-left overflow-auto"
                            style={{ height: "100%" }}
                        >
                            <AgGridReact
                                onGridReady={onGridReady}
                                onRowDataChanged={() => {
                                    if (exportRef.current) {
                                        exportTable();
                                        exportRef.current = false;
                                    }
                                    getSelectedRows();
                                }}
                                rowSelection="multiple"
                                getRowNodeId={(data) => data[data.length - 1]}
                                defaultColDef={{
                                    lockPosition: true,
                                }}
                                rowMultiSelectWithClick={true}
                                suppressRowClickSelection={true}
                                onRowClicked={handleRowClicked}
                                onSelectionChanged={onSelectionChanged}
                                rowData={mixedData}
                            >
                                <AgGridColumn
                                    field="0"
                                    headerName="Sample Name"
                                    pinned={true}
                                    width={300}
                                    maxWidth={700}
                                    resizable={true}
                                    headerCheckboxSelection={true}
                                    headerCheckboxSelectionFilteredOnly={true}
                                    checkboxSelection={true}
                                    cellClass="text-gray-500"
                                    filterParams={{
                                        newRowsAction: "keep",
                                    }}
                                    cellStyle={emptyHeaderCellStyle}
                                ></AgGridColumn>
                                {data &&
                                    data.metadata &&
                                    Object.keys(data.metadata).length > 0 && (
                                        <AgGridColumn
                                            field="metadata"
                                            headerName="Metadata"
                                            colId="metadata"
                                        >
                                            {getMetadataColumns()}
                                        </AgGridColumn>
                                    )}
                                <AgGridColumn
                                    headerName="Spectral Data"
                                    field="data"
                                    colId="data"
                                >
                                    {data &&
                                        data.headers &&
                                        data.headers.map((col, index) => (
                                            <AgGridColumn
                                                key={index}
                                                field={(
                                                    Object.keys(data.metadata)
                                                        .length +
                                                    index +
                                                    1
                                                ).toString()}
                                                headerName={
                                                    isNaN(col)
                                                        ? col
                                                        : col.toString()
                                                }
                                                cellRenderer={(params) =>
                                                    !isNaN(params.value) &&
                                                    (+params.value).toFixed(3)
                                                }
                                            ></AgGridColumn>
                                        ))}
                                </AgGridColumn>
                                {modifiedDataReference.headers.length > 0 && (
                                    <AgGridColumn
                                        field="reference"
                                        headerName={t("results")}
                                        colId="reference"
                                    >
                                        {modifiedDataReference.headers.map(
                                            (col, index) => (
                                                <AgGridColumn
                                                    key={col}
                                                    headerName={col}
                                                    cellStyle={emptyCellStyle}
                                                    field={(
                                                        Object.keys(
                                                            data.metadata
                                                        ).length +
                                                        data.headers.length +
                                                        index +
                                                        1
                                                    ).toString()}
                                                ></AgGridColumn>
                                            )
                                        )}
                                    </AgGridColumn>
                                )}
                            </AgGridReact>
                        </div>
                    </div>
                </LoadingOverlay>

                <div className="steps-btn flex items-center justify-end mt-4">
                    <button
                        disabled={!selectedRows.length}
                        onClick={() => setSpectraModalVisible(true)}
                        className={`default-orange-outline-btn ${
                            !selectedRows.length ? "disabled" : ""
                        }`}
                    >
                        <span className="mr-1">
                            <Trans i18nKey="plotSpectra">Plot Spectra</Trans>
                        </span>
                        {selectedRows.length > 0 && (
                            <span>({selectedRows.length})</span>
                        )}
                    </button>
                    <button
                        disabled={!selectedRows.length}
                        onClick={() => setDeleteModalVisible(true)}
                        className="default-orange-btn"
                    >
                        <i className="fas fa-trash-alt" />
                        <span className="ml-2 mr-1">
                            <Trans i18nKey="delete">Delete</Trans>
                        </span>
                        {selectedRows.length > 0 && (
                            <span>({selectedRows.length})</span>
                        )}
                    </button>
                    <button
                        onClick={cancelSelection}
                        className="default-transparent-btn"
                    >
                        <Trans i18nKey="cancelSelection">
                            Cancel Selection
                        </Trans>
                    </button>
                </div>
            </div>

            <MeasurementDeleteModal
                modalButton={deleteModalVisible}
                modalButtonSetter={setDeleteModalVisible}
                submitAction={(isLocal) => handleDelete(isLocal)}
                title={"Delete Measurement?"}
                message={`Are you sure you want to delete ${selectedRows.length} rows? This action cannot be undone!`}
            />
            <MeasurementsDetailsModal
                isVisible={detailsModalVisible}
                setIsVisible={setDetailsModalVisible}
                data={selectedRow}
                fields={fields}
                entityName={t("measurement")}
            />

            <BaseModal
                isVisible={spectraModalVisible}
                setIsVisible={setSpectraModalVisible}
                title={t("spectrum")}
            >
                <SpectrumChart
                    visible={true}
                    isSpectrumDisabled={isSpectrumModalDisabled}
                    renderSwitch={false}
                    enableSelectionButtons={false}
                    points={selectedSpectrumPoints}
                    hideBackground={true}
                    isWavenumber={isWavenumber}
                    isReflectance={isReflectance}
                />
            </BaseModal>
        </>
    );
};

export default DataTable;
